<template>
  <section class="pb-2">
    <e-filters
      ref="filters"
      :title="$t('Filtros')"
      :searching="fetching"
      :search-text="$t('Pesquisar')"
      @search="filterData"
      @reset="resetFiltersLocal"
    >
      <template #after_buttons>
        <e-button
          id="download"
          class="ml-1 btn-sm-block"
          variant="primary"
          icon="download"
          :busy="fetching"
          :text="$t('Baixar Relatório')"
          @click="downloadReport"
        />
        <!-- Button to download report (hidden by default) -->
        <a
          ref="downloadLink"
          style="display: none"
        />
      </template>
      <div>
        <b-row>
          <b-col md="6">
            <e-store-combo
              id="filters-store"
              v-model="filters.storeId"
              required
              @input="filters.checkingAccountId = null"
            />
          </b-col>

          <b-col md="6">
            <e-checking-account-combo
              v-model="filters.checkingAccountId"
              :type="['InternetBanking']"
              :store-id="filters.storeId"
              required
              active-options
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <FormulateInput
              id="period"
              v-model="filters.period"
              type="date-range-picker"
              :label="$t('Período')"
              class="required"
              :time-picker="false"
              validation="requiredrange"
              :validation-messages="{
                requiredrange: $t('Informe um período'),
              }"
            />
          </b-col>
        </b-row>
      </div>
    </e-filters>

    <b-card-actions
      action-refresh
      :show-loading="fetching"
      @refresh="filterData"
    >
      <div slot="title">
        <e-page-size-selector
          :per-page="paging.pageSize"
          :per-page-options="[10, 20, 50]"
          @change="pageSizeChange"
        />
      </div>
      <b-table
        ref="checking-account-table"
        show-empty
        responsive
        striped
        class="bordered"
        :fields="fields"
        :items="statements"
        :empty-text="getEmptyTableMessage($t('registro'), 'male', queryExecuted)"
        no-local-sorting
        :sort-by.sync="sorting.sortBy"
        :sort-desc.sync="sorting.sortDesc"
        @sort-changed="getData"
      >
        <template #cell(amount)="row">
          <span :class="row.item.creditDebitType === 'DEBITO' ? 'text-danger' : 'text-success'">
            {{
              (row.item.operationType === 'Debit' ? row.item.amount * -1 : row.item.amount)
                | currency
            }}
          </span>
        </template>
      </b-table>
      <b-row>
        <b-col cols="6">
          <e-pagination-summary
            :current-page="paging.currentPage"
            :per-page="paging.pageSize"
            :total="paging.rowCount"
            :total-on-page="paging.rowsInCurrentPage"
          />
        </b-col>
        <b-col cols="6">
          <b-pagination
            v-model="paging.currentPage"
            align="right"
            :total-rows="paging.rowCount"
            :per-page="paging.pageSize"
            aria-controls="checking-account-table"
            @change="pageChange"
          />
        </b-col>
      </b-row>
    </b-card-actions>
  </section>
</template>

<script>
import moment from 'moment'
import { mapState, mapActions } from 'vuex'
import { BRow, BCol, BPagination, BTable } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import {
  EPageSizeSelector,
  EPaginationSummary,
  EStoreCombo,
  EButton,
  EFilters,
  ECheckingAccountCombo,
} from '@/views/components'
import { alerts, financialDomain } from '@/mixins'

export default {
  components: {
    BRow,
    BCol,
    BCardActions,
    BPagination,
    BTable,
    EPaginationSummary,
    EPageSizeSelector,
    EStoreCombo,
    EFilters,
    EButton,
    ECheckingAccountCombo,
  },

  mixins: [alerts, financialDomain],

  data() {
    return {
      queryExecuted: false,
      fetching: false,
    }
  },

  computed: {
    ...mapState('pages/financial/internetBankingStatements', [
      'statements',
      'reportData',
      'paging',
      'sorting',
      'filters',
      'summaryFields',
    ]),

    fields() {
      return [
        {
          label: this.$t('Data'),
          key: 'transactionDate',
          thStyle: { width: '150px' },
          tdClass: 'text-center',
          // formatter: value => this.$options.filters.date(value),
        },
        {
          label: this.$t('Transação'),
          key: 'transactionName',
          tdClass: 'text-left',
        },
        {
          label: this.$t('Descrição'),
          key: 'historicComplement',
          tdClass: 'text-left',
        },
        {
          label: this.$t('Valor'),
          key: 'amount',
          tdClass: 'text-right',
          thStyle: { width: '120px' },
          formatter: val => this.$options.filters.currency(val),
        },
      ]
    },

    balances() {
      return {
        currentBalance: this.summaryFields?.currentBalance?.value,
        periodBalance: this.summaryFields?.periodBalance?.value,
        initialBalance: this.summaryFields?.initialBalance?.value,
        finalBalance: this.summaryFields?.finalBalance?.value,
      }
    },
  },

  mounted() {},

  methods: {
    ...mapActions('pages/financial/internetBankingStatements', [
      'fetchStatements',
      'generateReport',
      'setCurrentPage',
      'setPageSize',
      'resetFilters',
    ]),
    async getData() {
      this.$nextTick(async () => {
        try {
          this.fetching = true
          await this.fetchStatements()
          this.queryExecuted = true
        } catch (error) {
          this.showGenericError({ error })
        } finally {
          this.fetching = false
        }
      })
    },

    resetFiltersLocal() {
      this.resetFilters()
      this.getData()
    },
    filterData() {
      this.getData()
    },

    pageSizeChange(pageSize) {
      this.setPageSize(pageSize)
      this.getData()
    },

    pageChange(currentPage) {
      this.setCurrentPage(currentPage)
      this.getData()
    },

    showCheckingAccountInfo(item) {
      this.$refs.checkingAccountInfoSidebar.show(item)
    },

    createBankDeposit() {
      this.$refs.bankDepositSidebar.onShowSidebar()
    },
    createAccountTransfer() {
      this.$refs.accountTransferSidebar.onShowSidebar()
    },

    async downloadReport() {
      try {
        if (this.$refs.filters.isValid()) {
          this.fetching = true
          await this.generateReport()
          // Get the download link element from the template
          const { downloadLink } = this.$refs

          if (this.reportData) {
            // Set the URL and download attributes of the link
            downloadLink.href = this.reportData
            downloadLink.download = `Extrato_Loja_${this.filters.storeId}_IdConta_${
              this.filters.checkingAccountId
            }_${moment().format()}.xlsx`

            // Click the link to start the download
            downloadLink.click()
          } else {
            this.showError({ message: this.$t('Não foi possível recuperar os dados do relatório') })
          }
        }
      } catch (error) {
        this.showError({ error })
      } finally {
        this.fetching = false
      }
    },
  },
}
</script>

<style></style>
